import { Link } from "@tanstack/react-router";

export function TermsPage() {
  const lastUpdated = "November 4, 2026";

  return (
    <main className="tj-container max-w-4xl py-12 px-4 sm:px-6" data-testid="terms-page">
      {/* Breadcrumb Navigation */}
      <div className="mb-6">
        <Link
          to="/"
          className="text-xs font-semibold text-[var(--accent-terracotta)] hover:underline inline-flex items-center gap-1.5"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Home
        </Link>
      </div>

      {/* Header */}
      <header className="border-b border-[var(--border-warm)] pb-8 mb-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-orange-200/80 bg-orange-50/90 px-3.5 py-1 text-xs font-semibold text-[var(--accent-terracotta)] mb-4 shadow-xs">
          <span>Terms &amp; Conditions</span>
        </div>
        <h1 className="serif-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[var(--text-primary)]">
          Terms of Service
        </h1>
        <p className="mt-3 text-sm text-[var(--text-muted)]">
          Last updated: {lastUpdated} · Governed by the Laws of Hong Kong SAR
        </p>
      </header>

      {/* Terms Content */}
      <div className="space-y-8 text-sm sm:text-base text-[var(--text-secondary)] leading-relaxed">
        {/* Section 1: Acceptance & Legal Capacity */}
        <section className="space-y-3">
          <h2 className="serif-heading text-xl sm:text-2xl font-bold text-[var(--text-primary)]">
            1. Agreement to Terms
          </h2>
          <p>
            These Terms of Service (&ldquo;<strong>Terms</strong>&rdquo;) constitute a legally binding agreement between
            you (whether personally or on behalf of an entity) and Tensile Labs Limited (&ldquo;<strong>TaskJinn</strong>&rdquo;,
            &ldquo;<strong>we</strong>&rdquo;, &ldquo;<strong>us</strong>&rdquo;, or &ldquo;<strong>our</strong>&rdquo;),
            concerning your access to and use of the TaskJinn website, cloud orchestration platform, browser automation
            services, APIs, and client software (collectively, the &ldquo;<strong>Service</strong>&rdquo;).
          </p>
          <p>
            By creating an account, registering an authorized device, or using the Service, you signify your irrevocable
            acceptance of these Terms. If you do not agree to these Terms, you are expressly prohibited from using the
            Service and must discontinue use immediately.
          </p>
        </section>

        {/* Section 2: Prohibited Use & Illegal Purposes */}
        <section className="space-y-3">
          <h2 className="serif-heading text-xl sm:text-2xl font-bold text-[var(--text-primary)]">
            2. Acceptable Use: Absolute Prohibition on Illegal Purposes
          </h2>
          <div className="p-4 rounded-xl border border-rose-300 bg-rose-50/70 text-stone-900 space-y-2">
            <p className="font-bold text-rose-950 flex items-center gap-2">
              <span className="text-rose-700 font-extrabold">✕</span>
              Strict Prohibition: No Illegal, Malicious, or Unauthorized Activities
            </p>
            <p className="text-xs sm:text-sm text-stone-700">
              You agree never to use the Service, directly or indirectly, for any unlawful purpose, in violation of the
              laws of the Hong Kong Special Administrative Region or any applicable international jurisdiction.
            </p>
          </div>
          <p>Without limiting the generality of the foregoing, you shall NOT:</p>
          <ul className="list-disc pl-5 space-y-2 text-xs sm:text-sm">
            <li>
              Engage in unauthorized access, computer hacking, denial-of-service (DoS) attacks, penetration testing, or
              circumventing authentication or security boundaries of any target portal or network;
            </li>
            <li>
              Violate the Hong Kong Crimes Ordinance (Cap. 200) relating to computer misuse, unauthorized extraction of
              data, or obtaining access to computers with criminal or dishonest intent;
            </li>
            <li>
              Defraud, deceive, impersonate, harass, distribute malicious software, phishing payloads, or spam, or conduct
              unauthorized financial transactions;
            </li>
            <li>
              Infringe upon the copyright, patent, trademark, trade secret, or other proprietary rights of any third party;
            </li>
            <li>
              Circumvent or tamper with TaskJinn&rsquo;s platform metering, cryptographic protocols, token gates, or tenant
              isolation mechanisms.
            </li>
          </ul>
        </section>

        {/* Section 3: Right to Freeze or Terminate Accounts */}
        <section className="space-y-3">
          <h2 className="serif-heading text-xl sm:text-2xl font-bold text-[var(--text-primary)]">
            3. Account Suspension, Freezing &amp; Termination
          </h2>
          <p>
            We reserve the right, at our sole discretion, without liability, and without prior notice, to immediately{" "}
            <strong>freeze, suspend, restrict, or permanently terminate your account</strong>, revoke authorized device
            certificates, and cancel active automation tasks at any time, including but not limited to cases where:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-xs sm:text-sm">
            <li>You breach, or we have reasonable grounds to suspect that you have breached, any provision of these Terms;</li>
            <li>Your automation activities present a security hazard, abuse threshold, or regulatory liability to our infrastructure or third parties;</li>
            <li>We receive lawful orders or notices from law enforcement or judicial authorities in Hong Kong SAR; or</li>
            <li>There are outstanding fees, billing delinquencies, or fraudulent account authorizations.</li>
          </ul>
          <p className="text-xs sm:text-sm text-[var(--text-muted)]">
            Upon termination, your license to use the Service terminates immediately, and we may permanently delete your
            stored account settings and execution history.
          </p>
        </section>

        {/* Section 4: Strict Prohibition on Mission-Critical Work */}
        <section className="space-y-3 rounded-2xl bg-amber-50/70 border-2 border-amber-200/90 p-6 shadow-xs text-stone-900">
          <div className="flex items-center gap-2.5 text-amber-900 font-bold text-base sm:text-lg">
            <span className="text-xl">⚠️</span>
            <span>Important: Do Not Use for Mission-Critical Work</span>
          </div>
          <p className="text-xs sm:text-sm leading-relaxed">
            TaskJinn is designed and provided as an <strong>assistive, human-supervised browser automation tool</strong>.
            The Service is <strong>NOT</strong> fail-safe, fault-tolerant, or engineered for high-risk or mission-critical
            environments.
          </p>
          <p className="font-semibold text-xs sm:text-sm text-stone-950">
            You expressly agree NOT to use the Service for:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-xs sm:text-sm text-stone-800">
            <li>Life-support systems, medical devices, or clinical healthcare decision-making;</li>
            <li>Emergency services, public safety, air traffic, or mass transit control;</li>
            <li>Nuclear installations, hazardous chemical control, or autonomous industrial machinery;</li>
            <li>
              Unattended high-stakes financial trading, real-time banking transfers, or operations where a delay,
              failure, layout change, or browser interruption could result in death, personal injury, severe emotional harm,
              or catastrophic financial or property destruction.
            </li>
          </ul>
          <p className="text-xs text-stone-600">
            You remain exclusively responsible for reviewing, supervising, and verifying all automated actions, inputs,
            and outputs.
          </p>
        </section>

        {/* Section 5: Disclaimer of Warranties */}
        <section className="space-y-3">
          <h2 className="serif-heading text-xl sm:text-2xl font-bold text-[var(--text-primary)]">
            4. Disclaimer of Warranties (&ldquo;AS IS&rdquo; &amp; &ldquo;AS AVAILABLE&rdquo;)
          </h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY THE LAWS OF HONG KONG SAR:
          </p>
          <p className="text-xs sm:text-sm uppercase tracking-wide text-stone-700 bg-[var(--bg-card)] border border-[var(--border-warm)] p-4 rounded-xl leading-relaxed">
            THE SERVICE IS PROVIDED ON AN &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; BASIS WITHOUT WARRANTIES OF
            ANY KIND, EITHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE. WE EXPRESSLY DISCLAIM ALL WARRANTIES, INCLUDING
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, ACCURACY, NON-INFRINGEMENT, UNINTERRUPTED AVAILABILITY,
            FREEDOM FROM BUGS OR BOT-DETECTION COUNTERMEASURES, OR COMPATIBILITY WITH ANY THIRD-PARTY WEBSITE, PORTAL, OR
            BROWSER RUNTIME.
          </p>
        </section>

        {/* Section 6: Comprehensive Exclusion of Liability and Damages */}
        <section className="space-y-3">
          <h2 className="serif-heading text-xl sm:text-2xl font-bold text-[var(--text-primary)]">
            5. Total Exclusion of Damages &amp; Limitation of Liability
          </h2>
          <div className="rounded-xl border border-[var(--border-warm)] bg-[var(--bg-card)] p-5 space-y-3">
            <h3 className="font-bold text-[var(--text-primary)] text-sm sm:text-base">
              Comprehensive Damages Exclusions:
            </h3>
            <p className="text-xs sm:text-sm">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL TENSILE LABS LIMITED, ITS DIRECTORS,
              OFFICERS, EMPLOYEES, AFFILIATES, AGENTS, OR LICENSORS BE LIABLE FOR ANY:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-xs sm:text-sm text-stone-700">
              <li>
                <strong>INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, PUNITIVE, OR EXEMPLARY DAMAGES;</strong>
              </li>
              <li>
                <strong>LOSS OF PROFITS, REVENUE, SALES, BUSINESS, OR COMMERCIAL OPPORTUNITY;</strong>
              </li>
              <li>
                <strong>LOSS OF DATA, PRIVACY BREACHES ARISING ON TARGET WEBSITES, OR CORRUPTION OF DIGITAL ASSETS;</strong>
              </li>
              <li>
                <strong>BUSINESS INTERRUPTION, DOWNTIME, DELAYS, OR FAILURE OF TARGET WEBSITES TO COMPLETE TRANSACTIONS;</strong>
              </li>
              <li>
                <strong>
                  ACTIONS TAKEN BY THIRD-PARTY WEBSITES, INCLUDING ACCOUNT LOCKOUTS, CAPTCHA BLOCKS, IP BANS, OR 2FA
                  EXPIRATIONS RESULTING FROM AUTOMATION RUNS.
                </strong>
              </li>
            </ul>
            <p className="text-xs sm:text-sm pt-2 border-t border-[var(--border-warm)]">
              <strong>Aggregate Financial Liability Cap:</strong> In the event that liability cannot be fully excluded
              under applicable law, our aggregate cumulative liability for all claims arising out of or related to these
              Terms or the Service shall under no circumstances exceed the lesser of:
            </p>
            <div className="font-mono font-bold text-stone-900 bg-[var(--bg-subtle)] p-3 rounded-lg text-center text-sm">
              (A) The total fees paid by you to TaskJinn in the 12 months preceding the claim, or (B) HK$100.00
            </div>
          </div>
          <p className="text-xs text-[var(--text-muted)]">
            Nothing in these Terms limits or excludes liability that cannot legally be excluded under Hong Kong law
            (specifically under the Control of Exemption Clauses Ordinance, Cap. 71), including liability for death or
            personal injury caused by proven gross negligence or fraud.
          </p>
        </section>

        {/* Section 7: Indemnification */}
        <section className="space-y-3">
          <h2 className="serif-heading text-xl sm:text-2xl font-bold text-[var(--text-primary)]">
            6. Indemnification
          </h2>
          <p>
            You agree to defend, indemnify, and hold harmless Tensile Labs Limited, its directors, officers, employees, and
            contractors from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal
            fees) arising out of or in any way connected with: (a) your access to or use of the Service; (b) your violation
            of these Terms; (c) any unlawful, fraudulent, or prohibited automated activities conducted by your account; or
            (d) your violation of third-party portal terms, intellectual property rights, or privacy rights.
          </p>
        </section>

        {/* Section 8: Governing Law & Hong Kong Jurisdiction */}
        <section className="space-y-3 border-t border-[var(--border-warm)] pt-6">
          <h2 className="serif-heading text-xl sm:text-2xl font-bold text-[var(--text-primary)]">
            7. Governing Law &amp; Dispute Resolution
          </h2>
          <p>
            These Terms and any dispute or claim arising out of or in connection with them or their subject matter or
            formation (including non-contractual disputes or claims) shall be governed by and construed in accordance with
            the <strong>Laws of the Hong Kong Special Administrative Region</strong>.
          </p>
          <p>
            You irrevocably submit to the <strong>exclusive jurisdiction of the Courts of the Hong Kong Special
            Administrative Region</strong> for the resolution of any dispute or claim arising under or in connection with
            these Terms.
          </p>
        </section>

        {/* Section 9: General */}
        <section className="space-y-3 border-t border-[var(--border-warm)] pt-6">
          <h2 className="serif-heading text-xl sm:text-2xl font-bold text-[var(--text-primary)]">
            8. Contact Information
          </h2>
          <div className="bg-[var(--bg-card)] border border-[var(--border-warm)] rounded-xl p-4 text-xs sm:text-sm space-y-1">
            <p className="font-bold text-[var(--text-primary)]">Tensile Labs Limited (TaskJinn)</p>
            <p>Email: <a href="mailto:legal@tensilelabs.com" className="text-[var(--accent-terracotta)] hover:underline">legal@tensilelabs.com</a></p>
            <p>Jurisdiction: Hong Kong Special Administrative Region</p>
          </div>
        </section>
      </div>
    </main>
  );
}

